/**
 * Wave H1 — tactile feedback wrapper.
 *
 * Wraps @capacitor/haptics so callers can fire a one-line `tap("light")`
 * without thinking about platform detection or import-failure paths.
 *
 * - On Capacitor native (iOS/Android), invokes the real native haptic.
 * - On web (Vite dev / Capacitor live-reload from Safari), silently no-ops.
 * - On any error (plugin not yet linked into the iOS Pods, etc.), silently
 *   no-ops — haptics are pure polish, never block the user action.
 *
 * Suggested mapping for rating-style controls:
 *   • "heavy"  → a confident "yes" tap (slaps, like, favorite)
 *   • "medium" → a neutral selection (status picks, vibe taps)
 *   • "light"  → a soft confirmation (skip, dismiss, toggle)
 */
import { Capacitor } from "@capacitor/core";

export type HapticStyle = "light" | "medium" | "heavy";

let cachedHaptics: typeof import("@capacitor/haptics") | null | undefined = undefined;

async function loadHaptics(): Promise<typeof import("@capacitor/haptics") | null> {
  if (cachedHaptics !== undefined) return cachedHaptics;
  try {
    cachedHaptics = await import("@capacitor/haptics");
  } catch {
    // Plugin not present in this bundle (web preview, dev server, etc.).
    cachedHaptics = null;
  }
  return cachedHaptics;
}

/**
 * Fire a haptic tap. Returns immediately — does not block the calling
 * handler waiting for the native bridge.
 */
export function tap(style: HapticStyle = "medium"): void {
  if (!Capacitor.isNativePlatform?.()) return;
  // Fire and forget. We don't await — the user has already moved on.
  void (async () => {
    try {
      const mod = await loadHaptics();
      if (!mod) return;
      const { Haptics, ImpactStyle } = mod;
      const impact =
        style === "heavy"
          ? ImpactStyle.Heavy
          : style === "light"
            ? ImpactStyle.Light
            : ImpactStyle.Medium;
      await Haptics.impact({ style: impact });
    } catch {
      // Swallow — haptics are never load-bearing.
    }
  })();
}

/**
 * Fire a "success" notification haptic (a celebratory double-tap on iOS).
 * Reserved for completion moments like "scan finished" or "card shared".
 */
export function success(): void {
  if (!Capacitor.isNativePlatform?.()) return;
  void (async () => {
    try {
      const mod = await loadHaptics();
      if (!mod) return;
      const { Haptics, NotificationType } = mod;
      await Haptics.notification({ type: NotificationType.Success });
    } catch {
      // ignored
    }
  })();
}
